// Sales · Revenue & Growth home. HomeHero + 8 hero KPIs + Renewals-at-risk
// table + MRR-by-type bars + Upsell-opportunities table. Every number traces to
// a real endpoint (analytics/mrr, analytics/dashboard, analytics/churn,
// analytics/upsell, licenses); nothing is fabricated. No MRR sparkline is shown
// because the backend exposes no MRR time-series (honest over decorative).
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { HomeHero } from '@/components/shared/HomeHero';
import { Kpi, UtilBar, ProgressBar } from '@/components/shared/Kpi';
import { Card, CardHead, Table, Td, Row, Btn } from '@/components/shared/primitives';
import { TierPill, ChurnPill, Pill } from '@/components/shared/badges';
import { Icon } from '@/components/shared/Icon';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { money, moneyK } from '@/lib/format';
import { PERSONA } from '@/lib/persona';
import {
  getMrrAnalytics, getDashboardStats, getChurn, getUpsell, getLicenses,
  type MrrAnalytics, type DashboardStats, type LicenseAnalyticsRow, type LicenseListRow, type MrrRow,
} from '@/lib/api/sales';

const ACCENT = PERSONA.sales.accent;

function daysUntil(iso?: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / 86_400_000);
}

export function SalesHome() {
  const navigate = useNavigate();
  const [mrr, setMrr] = useState<MrrAnalytics | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [churn, setChurn] = useState<LicenseAnalyticsRow[]>([]);
  const [upsell, setUpsell] = useState<LicenseAnalyticsRow[]>([]);
  const [licenses, setLicenses] = useState<LicenseListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, s, c, u, l] = await Promise.all([
        getMrrAnalytics(),
        getDashboardStats().catch(() => null),
        getChurn().catch(() => []),
        getUpsell().catch(() => []),
        getLicenses().catch(() => []),
      ]);
      setMrr(m);
      setStats(s);
      setChurn(c);
      setUpsell(u);
      setLicenses(l);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load revenue data');
      setMrr(null); setStats(null); setChurn([]); setUpsell([]); setLicenses([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const mrrByLicense = useMemo(() => {
    const map = new Map<string, MrrRow>();
    (mrr?.rows || []).forEach((r) => map.set(r.license_id, r));
    return map;
  }, [mrr]);
  const licById = useMemo(() => {
    const map = new Map<string, LicenseListRow>();
    licenses.forEach((l) => map.set(l.id, l));
    return map;
  }, [licenses]);

  const atRisk = useMemo(
    () => churn
      .filter((a) => a.churn_risk_level === 'high' || a.churn_risk_level === 'critical')
      .sort((a, b) => (b.churn_probability || 0) - (a.churn_probability || 0)),
    [churn],
  );
  const atRiskMrr = useMemo(
    () => atRisk.reduce((sum, a) => sum + (mrrByLicense.get(a.license_id)?.total_mrr || 0), 0),
    [atRisk, mrrByLicense],
  );
  const upsellHigh = useMemo(
    () => upsell
      .filter((a) => a.upsell_opportunity && a.upsell_opportunity !== 'none')
      .sort((a, b) => (b.upsell_score || 0) - (a.upsell_score || 0)),
    [upsell],
  );

  const totalMrr = mrr?.total_mrr ?? 0;
  const maxType = Math.max(1, ...(mrr?.by_customer_type || []).map((t) => t.mrr));

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
        <div className="animate-spin" style={{ width: 32, height: 32, border: '2px solid var(--persona-sales)', borderTopColor: 'transparent', borderRadius: '50%' }} />
      </div>
    );
  }

  return (
    <div>
      <HomeHero
        persona="sales"
        title="Revenue & Growth"
        subtitle="How the book of business is performing — today"
        actions={
          <>
            <Btn icon="download" disabled title="Sales export is not yet supported.">Export</Btn>
            <Btn kind="primary" accent={ACCENT} icon="plus" onClick={() => navigate('/sales/quotes/new')}>New License</Btn>
          </>
        }
      />

      {error && <div style={{ marginBottom: 16 }}><AlertBanner kind="error">Unable to load revenue data: {error}</AlertBanner></div>}

      {/* Hero KPIs (8) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 14 }}>
        <Kpi label="MRR" value={moneyK(totalMrr)} hint="monthly recurring" accent={ACCENT} icon="dollar-sign" />
        <Kpi label="ARR" value={moneyK(totalMrr * 12)} hint="MRR × 12" accent={ACCENT} icon="trending-up" />
        <Kpi label="Active Customers" value={stats?.activeOrganizations ?? 0} hint="active organizations" accent={ACCENT} icon="building-2" />
        <Kpi label="Net New Revenue" value={moneyK(mrr?.overage_revenue ?? 0)} hint="overage this period" accent={ACCENT} icon="plus-circle" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
        <Kpi label="Trials Active" value={mrr?.trials_active ?? 0} hint="in pipeline" accent={ACCENT} icon="gift" onClick={() => navigate('/sales/trials')} />
        <Kpi label="Conversion Rate" value={Math.round((mrr?.conversion_rate ?? 0) * 100)} unit="%" accent={ACCENT} icon="target" />
        <Kpi label="At-Risk MRR" value={moneyK(atRiskMrr)} hint={`${atRisk.length} account${atRisk.length !== 1 ? 's' : ''}`} accent={ACCENT} icon="alert-triangle" onClick={() => navigate('/sales/renewals')} />
        <Kpi label="Upsell Pipeline" value={upsellHigh.length} hint="high-fit accounts" accent={ACCENT} icon="arrow-up-circle" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Renewals at risk */}
        <Card style={{ overflow: 'hidden' }}>
          <CardHead
            title="Renewals at risk"
            sub="Highest churn probability first"
            right={<a onClick={() => navigate('/sales/renewals')} style={linkS}>View all <Icon name="arrow-right" size={12} /></a>}
          />
          {atRisk.length === 0 ? (
            <div style={empty}>No accounts at high or critical churn risk.</div>
          ) : (
            <Table cols={[{ label: 'Account' }, { label: 'MRR', align: 'right' }, { label: 'Renewal' }, { label: 'Churn', align: 'right' }]}>
              {atRisk.slice(0, 5).map((a) => {
                const r = mrrByLicense.get(a.license_id);
                const lic = licById.get(a.license_id);
                const dleft = daysUntil(lic?.expires_at);
                const name = a.license?.organization?.name || r?.customer_name || 'Unknown';
                const tier = a.license?.tier || r?.customer_type || 'trial';
                return (
                  <Row key={a.license_id} onClick={() => navigate(`/sales/licenses/${a.license_id}`)}>
                    <Td>
                      <div style={{ fontWeight: 600 }}>{name}</div>
                      <div style={{ marginTop: 2 }}><TierPill tier={String(tier)} /></div>
                    </Td>
                    <Td align="right" style={{ fontWeight: 600 }}>{r ? money(r.total_mrr) : '—'}</Td>
                    <Td>{dleft != null ? <span style={{ color: dleft <= 15 ? 'var(--status-expired)' : 'var(--text-secondary)', fontWeight: dleft <= 15 ? 600 : 400 }}>{dleft}d</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</Td>
                    <Td align="right"><ChurnPill level={a.churn_risk_level || 'low'} /></Td>
                  </Row>
                );
              })}
            </Table>
          )}
        </Card>

        {/* MRR by customer type */}
        <Card>
          <CardHead title="MRR by customer type" />
          <div style={{ padding: '16px 20px' }}>
            {(mrr?.by_customer_type || []).length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No MRR recorded yet.</div>
            ) : (
              (mrr?.by_customer_type || []).map((t) => (
                <div key={t.customer_type} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{t.customer_type} <span style={{ color: 'var(--text-muted)' }}>· {t.license_count}</span></span>
                    <span style={{ fontWeight: 700 }}>{moneyK(t.mrr)}</span>
                  </div>
                  <ProgressBar pct={(t.mrr / maxType) * 100} color={ACCENT} height={7} />
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Upsell opportunities */}
      <Card style={{ overflow: 'hidden' }}>
        <CardHead
          title="Upsell opportunities"
          sub="Accounts running hot — high utilization, active usage"
          right={<a onClick={() => navigate('/sales/accounts')} style={linkS}>All accounts <Icon name="arrow-right" size={12} /></a>}
        />
        {upsellHigh.length === 0 ? (
          <div style={empty}>No upsell opportunities surfaced yet.</div>
        ) : (
          <Table cols={[{ label: 'Account' }, { label: 'Tier' }, { label: 'Vendor usage' }, { label: 'MRR', align: 'right' }, { label: 'Fit', align: 'right' }]}>
            {upsellHigh.slice(0, 8).map((a) => {
              const r = mrrByLicense.get(a.license_id);
              const name = a.license?.organization?.name || r?.customer_name || 'Unknown';
              const tier = a.license?.tier || r?.customer_type || 'trial';
              return (
                <Row key={a.license_id} onClick={() => navigate(`/sales/licenses/${a.license_id}`)}>
                  <Td style={{ fontWeight: 600 }}>{name}</Td>
                  <Td><TierPill tier={String(tier)} /></Td>
                  <Td style={{ width: 200 }}>{r && r.vendor_pool ? <UtilBar used={r.vendor_usage} total={r.vendor_pool} /> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</Td>
                  <Td align="right" style={{ fontWeight: 600 }}>{r ? money(r.total_mrr) : '—'}</Td>
                  <Td align="right"><Pill bg={PERSONA.sales.tint} fg={ACCENT}>{a.upsell_opportunity} fit</Pill></Td>
                </Row>
              );
            })}
          </Table>
        )}
      </Card>
    </div>
  );
}

const linkS: React.CSSProperties = { fontSize: 12.5, color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' };
const empty: React.CSSProperties = { padding: '28px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 };

export default SalesHome;
